import React, { useState, useEffect } from "react";
import DashboardHeaderCard from "@/components/common/DashboardHeaderCard";
import { Users, UserCheck, UserX, UserCog } from "lucide-react";
import userService from "@/services/userService";

const UserManagementHeader = ({ users }) => {
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    inactiveUsers: 0,
    uniqueRoles: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUserStats = async () => {
      try {
        setLoading(true);
        const statsData = await userService.getDashboardStatistics();
        setStats({
          totalUsers: statsData.total_users || 0,
          activeUsers: statsData.active_users || 0,
          inactiveUsers: statsData.inactive_users || 0,
          uniqueRoles: Object.keys(statsData.by_role || {}).length
        });
      } catch (error) {
        console.error("Failed to fetch dashboard statistics:", error);
        try {
          const allUsersData = await userService.getAllUsers();
          let allUsers = [];
          if (allUsersData && typeof allUsersData === 'object' && Array.isArray(allUsersData.data)) {
            allUsers = allUsersData.data;
          } else if (Array.isArray(allUsersData)) {
            allUsers = allUsersData;
          } else if (allUsersData && typeof allUsersData === 'object' && Array.isArray(allUsersData.users)) {
            allUsers = allUsersData.users;
          }
          const totalUsers = allUsers.length;
          const activeUsers = allUsers.filter(user => user.is_active).length;
          const inactiveUsers = totalUsers - activeUsers;
          const uniqueRoles = [...new Set(allUsers.map(user => user.role))].filter(role => role).length;
          setStats({
            totalUsers,
            activeUsers,
            inactiveUsers,
            uniqueRoles
          });
        } catch (fallbackError) {
          console.error("Fallback method also failed:", fallbackError);
          const totalUsers = users.length;
          const activeUsers = users.filter(user => user.status === 'Active').length;
          const inactiveUsers = users.filter(user => user.status === 'Inactive').length;
          const uniqueRoles = [...new Set(users.map(user => user.role))].filter(role => role).length;
          setStats({
            totalUsers,
            activeUsers,
            inactiveUsers,
            uniqueRoles
          });
        }
      } finally {
        setLoading(false);
      }
    };

    fetchUserStats();
  }, []);

  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 w-full">
        <DashboardHeaderCard
          name="TOTAL USERS"
          count="0"
          icon={<Users size={18} color="hsl(var(--primary))" />}
          differenceText="System-wide user count"
        />

        <DashboardHeaderCard
          name="ACTIVE USERS"
          count="0"
          icon={<UserCheck size={18} color="hsl(var(--primary))" />}
          differenceText="Currently active accounts"
        />

        <DashboardHeaderCard
          name="INACTIVE USERS"
          count="0"
          icon={<UserX size={18} color="hsl(var(--primary))" />}
          differenceText="Currently inactive accounts"
        />

        <DashboardHeaderCard
          name="USER ROLES"
          count="0"
          icon={<UserCog size={18} color="hsl(var(--primary))" />}
          differenceText="Different user roles in system"
        />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 w-full">
      <DashboardHeaderCard
        name="TOTAL USERS"
        count={stats.totalUsers.toString()}
        icon={<Users size={18} color="hsl(var(--primary))" />}
        differenceText="System-wide user count"
      />

      <DashboardHeaderCard
        name="ACTIVE USERS"
        count={stats.activeUsers.toString()}
        icon={<UserCheck size={18} color="hsl(var(--primary))" />}
        differenceText="Currently active accounts"
      />

      <DashboardHeaderCard
        name="INACTIVE USERS"
        count={stats.inactiveUsers.toString()}
        icon={<UserX size={18} color="hsl(var(--primary))" />}
        differenceText="Currently inactive accounts"
      />

      <DashboardHeaderCard
        name="USER ROLES"
        count={stats.uniqueRoles.toString()}
        icon={<UserCog size={18} color="hsl(var(--primary))" />}
        differenceText="Different user roles in system"
      />
    </div>
  );
};

export default UserManagementHeader;